import { defineStore } from 'pinia';
import type { IAuthStore } from '../../../shared/interfaces/auth-store.interface';
import type { IUser } from '../../../shared/interfaces/user.iterface';
import { type AccountInfo } from '@azure/msal-browser';
import { cloneDeep, first, get, split, toString } from 'lodash';

const EXTENSION_GROUPS_KEY: string = (import.meta.env.VITE_AZURE_B2C_GROUP_PROPERTY as string) || 'extension_groups';

const initialStateAuthStore: IAuthStore = {
    isAuthenticated: false,
    user: null,
    token: '',
    idp_access_token: '',
    msalUser: null,
    access_token_claims: {},
    roles: [],
    groups: [],
};

const useAuthStore = defineStore('auth', {
    state: () => ({ ...initialStateAuthStore }),
    getters: {
        selectUser: (state): IUser | null => state.user,
        selectMSALUser: (state): AccountInfo | null => state.msalUser,
        selectMSALUserRoles: (state): string[] | null => state.msalUser?.idTokenClaims?.roles ?? null,
        selectMSALUserName: (state): string =>
            toString(first(split(toString(get(state.msalUser, 'idTokenClaims.given_name')), ' '))),
        selectAccessTokenUserName: (state): string =>
            toString(get(state.access_token_claims, 'given_name')) !== ''
                ? toString(first(split(toString(get(state.access_token_claims, 'given_name')), ' ')))
                : toString(first(split(toString(get(state.access_token_claims, 'name')), ' '))),
        selectIsAuthenticated: (state): boolean => state.isAuthenticated,
        selectToken: (state): string => state.token,
        selectIpdAccessToken: (state): string => state.idp_access_token,

        selectAccessTokenClaims: (state): any => state.access_token_claims,

        selectAccessTokenRoles: (state): any => state.access_token_claims?.[EXTENSION_GROUPS_KEY],

        selectAccessTokenGroups: (state): any => state.access_token_claims?.groups ?? [],
    },
    actions: {
        setUser(user: IUser) {
            this.user = user;
        },
        resetUser() {
            this.user = initialStateAuthStore.user;
        },
        setMSALUser(msalUser: AccountInfo) {
            const cloneUser = cloneDeep(msalUser);
            this.msalUser = cloneUser;
        },
        resetMSALUser() {
            this.msalUser = null;
        },
        setToken(token: string) {
            this.token = token;
        },
        setIpdAccessToken(idp_access_token: string) {
            this.idp_access_token = idp_access_token;
        },
        resetIpdAccessToken() {
            this.idp_access_token = '';
        },

        setAccessTokenClaims(access_token_claims: any) {
            this.access_token_claims = access_token_claims;
        },

        resetAccessTokenClaims() {
            this.access_token_claims = {};
        },
        resetToken() {
            this.token = '';
        },
        setAuthenticated(authenticated: boolean) {
            this.isAuthenticated = authenticated;
        },
        resetAuthenticated() {
            this.isAuthenticated = false;
        },
    },
});

export default useAuthStore;
